import { useState, useEffect, useRef } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Wind, Eye, MessageCircle, Crosshair, Pencil, Plus, Trash2, Play, Pause, SkipForward, GripVertical, Clock, X, Check, Trophy, ChevronUp, ChevronDown, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_TYPES = [
  { value: "breathing", label: "Breathing", icon: Wind, color: "bg-teal-100 text-teal-700" },
  { value: "visualization", label: "Visualization", icon: Eye, color: "bg-indigo-100 text-indigo-700" },
  { value: "affirmation", label: "Affirmation", icon: MessageCircle, color: "bg-amber-100 text-amber-700" },
  { value: "cue_word", label: "Cue Word", icon: Crosshair, color: "bg-rose-100 text-rose-700" },
  { value: "custom", label: "Custom", icon: Pencil, color: "bg-gray-100 text-gray-700" },
];

const STEP_DEFAULTS = {
  breathing: { title: "Box Breathing", description: "Inhale 4s → Hold 4s → Exhale 4s → Hold 4s", duration_seconds: 60 },
  visualization: { title: "Performance Visualization", description: "See yourself executing perfectly", duration_seconds: 90 },
  affirmation: { title: "Power Affirmations", description: "Repeat your key affirmations with conviction", duration_seconds: 45 },
  cue_word: { title: "Cue Word", description: "Your trigger word to lock in focus", duration_seconds: 15 },
  custom: { title: "Custom Step", description: "", duration_seconds: 60 },
};

function getStepMeta(type) {
  return STEP_TYPES.find((t) => t.value === type) || STEP_TYPES[4];
}

// ---- Play Mode Full-Screen ----
function RoutinePlayer({ routine, onComplete, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);

  const step = routine.steps[stepIdx];
  const meta = getStepMeta(step?.type);
  const Icon = meta.icon;

  const gradients = {
    breathing: "from-teal-600 to-emerald-700",
    visualization: "from-indigo-600 to-purple-700",
    affirmation: "from-amber-600 to-orange-700",
    cue_word: "from-rose-600 to-pink-700",
    custom: "from-gray-600 to-slate-700",
  };

  useEffect(() => {
    if (step) setTimeLeft(step.duration_seconds);
  }, [stepIdx, step]);

  useEffect(() => {
    if (!playing || timeLeft <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          if (stepIdx < routine.steps.length - 1) {
            setTimeout(() => setStepIdx((i) => i + 1), 500);
          } else {
            setDone(true);
            setPlaying(false);
            markComplete();
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [playing, timeLeft]);

  const markComplete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/pre-routines/${routine.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { console.error("Failed to mark routine complete:", e); }
    if (onComplete) onComplete();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const pct = step ? ((step.duration_seconds - timeLeft) / step.duration_seconds) * 100 : 0;

  if (done) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <Trophy className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Routine Complete!</h2>
          <p className="text-white/80 mb-8">{routine.title}</p>
          <Button data-testid="routine-done-btn" onClick={onClose} className="bg-white text-emerald-700 hover:bg-white/90 px-8 py-3">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 bg-gradient-to-br ${gradients[step?.type] || gradients.custom} flex flex-col`}>
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center space-x-2">
          {routine.steps.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i === stepIdx ? "w-8 bg-white" : i < stepIdx ? "w-4 bg-white/60" : "w-4 bg-white/20"}`} />
          ))}
        </div>
        <button data-testid="routine-player-close" onClick={onClose} className="text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-4 ring-2 ring-white/30">
          <Icon className="w-8 h-8 text-white" />
        </div>
        <p className="text-sm font-medium text-white/70 uppercase tracking-wider mb-1">Step {stepIdx + 1} of {routine.steps.length}</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{step.title}</h2>
        <p className="text-white/80 text-base max-w-md leading-relaxed my-4">{step.description}</p>

        <div className="mt-4">
          <div className="w-64 h-2 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white/70 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-white text-2xl font-mono mt-3">{fmt(timeLeft)}</p>
        </div>
      </div>

      <div className="flex items-center justify-center space-x-6 pb-8">
        <Button data-testid="routine-play-btn" onClick={() => setPlaying(!playing)} className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 border-2 border-white/40">
          {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white ml-0.5" />}
        </Button>
        {stepIdx < routine.steps.length - 1 && (
          <Button data-testid="routine-skip-btn" onClick={() => { setStepIdx(stepIdx + 1); setPlaying(false); }} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/30">
            <SkipForward className="w-5 h-5 text-white" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ---- Main Page ----
export default function PrePerformanceRoutines({ user, onLogout }) {
  const [routines, setRoutines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [playingRoutine, setPlayingRoutine] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiTiming, setAiTiming] = useState("before_warmup");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sportContext, setSportContext] = useState("");
  const [steps, setSteps] = useState([]);

  useEffect(() => { fetchRoutines(); }, []);

  const fetchRoutines = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/pre-routines`, { headers: { Authorization: `Bearer ${token}` } });
      setRoutines(res.data);
    } catch { toast.error("Failed to load routines"); }
  };

  const openCreate = () => {
    setTitle("");
    setDescription("");
    setSportContext("");
    setSteps([
      { id: crypto.randomUUID(), ...STEP_DEFAULTS.breathing, type: "breathing", order: 0 },
      { id: crypto.randomUUID(), ...STEP_DEFAULTS.visualization, type: "visualization", order: 1 },
      { id: crypto.randomUUID(), ...STEP_DEFAULTS.affirmation, type: "affirmation", order: 2 },
    ]);
    setEditingRoutine(null);
    setShowCreate(true);
  };

  const handleAiGenerate = async (timing) => {
    setAiGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/pre-routines/ai-generate`, 
        { timing: timing || aiTiming },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`AI routine created for ${timing === "during_warmup" ? "during warm-ups" : "before warm-ups"}!`);
      fetchRoutines();
    } catch (e) {
      toast.error("AI generation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setAiGenerating(false);
    }
  };

  const openEdit = (r) => {
    setTitle(r.title);
    setDescription(r.description);
    setSportContext(r.sport_context);
    setSteps(r.steps.map((s, i) => ({ ...s, id: s.id || crypto.randomUUID(), order: i })));
    setEditingRoutine(r);
    setShowCreate(true);
  };

  const addStep = (type) => {
    setSteps([...steps, { id: crypto.randomUUID(), ...STEP_DEFAULTS[type], type, order: steps.length }]);
  };

  const removeStep = (id) => setSteps(steps.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })));
  const moveStep = (idx, dir) => {
    const arr = [...steps];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setSteps(arr.map((s, i) => ({ ...s, order: i })));
  };
  const updateStep = (id, field, value) => setSteps(steps.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Please enter a routine title"); return; }
    if (steps.length === 0) { toast.error("Add at least one step"); return; }

    try {
      const token = localStorage.getItem("token");
      const payload = { title, description, sport_context: sportContext, steps };
      if (editingRoutine) {
        await axios.put(`${API}/pre-routines/${editingRoutine.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Routine updated");
      } else {
        await axios.post(`${API}/pre-routines`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Routine created!");
      }
      setShowCreate(false);
      fetchRoutines();
    } catch { toast.error("Failed to save routine"); }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/pre-routines/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Routine deleted");
      fetchRoutines();
    } catch { toast.error("Failed to delete routine"); }
  };

  const totalDuration = (stps) => stps.reduce((a, s) => a + (s.duration_seconds || 0), 0);
  const fmtDur = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${s % 60 ? `${s % 60}s` : ""}`.trim() : `${s}s`;

  if (playingRoutine) {
    return <RoutinePlayer routine={playingRoutine} onComplete={fetchRoutines} onClose={() => setPlayingRoutine(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-10">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-1">Pre-Performance Routines</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Build and practice your personalized game-day routines</p>
          </div>
          <Button data-testid="create-routine-btn" onClick={openCreate} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-1.5" /> New Routine
          </Button>
        </div>

        {/* AI Coach Section */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 mb-6 border border-indigo-100">
          <div className="flex items-center space-x-2 mb-3">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <h3 className="font-bold text-gray-900 text-base">AI Coach — Generate Routine</h3>
          </div>
          <p className="text-gray-600 text-sm mb-3">3-step routine personalized to your training plan, goals &amp; sport. Choose when you&apos;ll use it:</p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center bg-white rounded-lg p-1 shadow-sm">
              <button
                data-testid="timing-before"
                onClick={() => setAiTiming("before_warmup")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  aiTiming === "before_warmup"
                    ? "bg-indigo-600 text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Before Warm-Up
              </button>
              <button
                data-testid="timing-during"
                onClick={() => setAiTiming("during_warmup")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  aiTiming === "during_warmup"
                    ? "bg-indigo-600 text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                During Warm-Up
              </button>
            </div>
            <Button
              data-testid="ai-generate-routine-btn"
              onClick={() => handleAiGenerate(aiTiming)}
              disabled={aiGenerating}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {aiGenerating ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1.5" /> Generate Routine</>
              )}
            </Button>
          </div>
        </div>

        {/* Routine Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {routines.map((r) => {
            const stepTypes = r.steps.map((s) => getStepMeta(s.type));
            return (
              <Card key={r.id} data-testid={`routine-card-${r.id}`} className="border-0 shadow-md bg-white/90 backdrop-blur-sm hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-bold text-gray-900 text-base">{r.title}</h3>
                        {r.ai_generated && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700"><Sparkles className="w-2.5 h-2.5 mr-0.5" />AI</span>}
                        {r.timing && <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.timing === "during_warmup" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>{r.timing === "during_warmup" ? "During Warm-Up" : "Pre Warm-Up"}</span>}
                      </div>
                      {r.sport_context && <p className="text-xs text-gray-500">{r.sport_context}</p>}
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button onClick={() => openEdit(r)} className="p-1.5 text-gray-400 hover:text-gray-600"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(r.id)} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {r.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{r.description}</p>}

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {stepTypes.map((m, i) => (
                      <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.color}`}>
                        <m.icon className="w-3 h-3 mr-1" />{m.label}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1" />{fmtDur(r.total_duration_seconds || totalDuration(r.steps))}</span>
                      <span>{r.steps.length} steps</span>
                      {r.times_used > 0 && <span>Used {r.times_used}x</span>}
                    </div>
                    <Button data-testid={`play-routine-${r.id}`} size="sm" onClick={() => setPlayingRoutine(r)} className="bg-teal-600 hover:bg-teal-700 h-8 px-3">
                      <Play className="w-3.5 h-3.5 mr-1" /> Start
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {routines.length === 0 && (
          <div className="text-center py-16">
            <Wind className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No routines yet</h3>
            <p className="text-gray-500 mb-4 max-w-md mx-auto">Create your first pre-performance routine to get mentally ready before games and competitions</p>
            <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1.5" /> Create Routine</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRoutine ? "Edit Routine" : "Create Routine"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="routine-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Game Day Focus" />
            </div>
            <div>
              <Label>Sport Context (optional)</Label>
              <Input value={sportContext} onChange={(e) => setSportContext(e.target.value)} placeholder="e.g. Basketball, Tennis" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this routine for?" rows={2} />
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Steps ({fmtDur(totalDuration(steps))} total)</Label>
              </div>

              <div className="space-y-2">
                {steps.map((step, idx) => {
                  const meta = getStepMeta(step.type);
                  return (
                    <div key={step.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex flex-col">
                          <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                          <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
                          <meta.icon className="w-3 h-3 mr-1" />{meta.label}
                        </span>
                        <Input value={step.title} onChange={(e) => updateStep(step.id, "title", e.target.value)} className="flex-1 h-8 text-sm" />
                        <div className="flex items-center space-x-1">
                          <Input
                            type="number"
                            min={5}
                            value={step.duration_seconds}
                            onChange={(e) => updateStep(step.id, "duration_seconds", parseInt(e.target.value) || 5)}
                            className="w-16 h-8 text-sm text-center"
                          />
                          <span className="text-xs text-gray-500">sec</span>
                        </div>
                        <button onClick={() => removeStep(step.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <Textarea
                        value={step.description}
                        onChange={(e) => updateStep(step.id, "description", e.target.value)}
                        placeholder="Instructions for this step..."
                        rows={1}
                        className="text-sm"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Add step buttons */}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {STEP_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => addStep(t.value)}
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${t.color} hover:opacity-80 transition`}
                  >
                    <Plus className="w-3 h-3 mr-1" />{t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex space-x-3 pt-2">
              <Button data-testid="save-routine-btn" onClick={handleSave} className="flex-1 bg-teal-600 hover:bg-teal-700">
                {editingRoutine ? "Update Routine" : "Create Routine"}
              </Button>
              <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
